"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { useLocation } from "@/contexts/location-context";
import { api } from "../../convex/_generated/api";
import { db } from "@/lib/dexie-db";
import { SHARED_CACHE } from "@/lib/cache-config";
import { basemapTileUrl } from "@/lib/map-basemap";
import { ensureSdoHmiImageCached } from "@/lib/sdo-hmi-image-cache";
import { useSafeQuery } from "@/hooks/use-safe-query";

const PREFETCH_STATUS_KEY = "aurora-tonight-prefetch-v1";
const OFFLINE_READY_HOURS = 6;

type PrefetchStatus = {
    readyUntil: number | null;
    lastPrefetchAt: number | null;
    isPrefetching: boolean;
    progress: number;
    error: string | null;
};

export type TonightPrefetchValue = PrefetchStatus & {
    isReady: boolean;
    /** True when auto-prefetch is skipped due to cellular / save-data. */
    autoSkippedReason: "cellular" | "saveData" | "offline" | null;
    runPrefetch: (options?: { force?: boolean }) => Promise<void>;
};

const TonightPrefetchContext = createContext<TonightPrefetchValue | null>(null);

function readStoredReadyUntil(): number | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(PREFETCH_STATUS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { readyUntil?: number };
        return typeof parsed.readyUntil === "number" ? parsed.readyUntil : null;
    } catch {
        return null;
    }
}

function writeStoredReadyUntil(readyUntil: number): void {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
        PREFETCH_STATUS_KEY,
        JSON.stringify({ readyUntil, savedAt: Date.now() }),
    );
}

function getConnectionHints(): {
    saveData: boolean;
    cellular: boolean;
    offline: boolean;
} {
    if (typeof navigator === "undefined") {
        return { saveData: false, cellular: false, offline: false };
    }
    const connection = (
        navigator as Navigator & {
            connection?: { type?: string; effectiveType?: string; saveData?: boolean };
        }
    ).connection;
    return {
        saveData: Boolean(connection?.saveData),
        cellular: connection?.type === "cellular",
        offline: navigator.onLine === false,
    };
}

/** Warm dark basemap tiles around a lat/lon for offline map use. */
async function prefetchMapTiles(lat: number, lon: number, zooms: number[] = [5, 6, 7]): Promise<void> {
    const n = (z: number, latDeg: number, lonDeg: number) => {
        const latRad = (latDeg * Math.PI) / 180;
        const xtile = Math.floor(((lonDeg + 180) / 360) * Math.pow(2, z));
        const ytile = Math.floor(
            ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
                Math.pow(2, z),
        );
        return { x: xtile, y: ytile };
    };

    const urls: string[] = [];
    for (const z of zooms) {
        const { x, y } = n(z, lat, lon);
        for (let dx = -1; dx <= 1; dx += 1) {
            for (let dy = -1; dy <= 1; dy += 1) {
                urls.push(basemapTileUrl("dark", z, x + dx, y + dy, "base"));
            }
        }
    }

    await Promise.allSettled(
        urls.map(
            (url) =>
                new Promise<void>((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                    img.src = url;
                }),
        ),
    );
}

async function countFreshCacheEntries(): Promise<number> {
    const now = Date.now();
    const keys = Object.values(SHARED_CACHE);
    let fresh = 0;
    for (const entry of keys) {
        const table = db[entry.table];
        if (!table) continue;
        const row = await table.where("queryKey").equals(entry.cacheKey).first();
        if (row && row.expiresAt > now - entry.ttl) {
            fresh += 1;
        } else if (row) {
            fresh += 0.5;
        }
    }
    return fresh;
}

/**
 * Best-effort SDO URL for offline prefetch. Uses a non-throwing subscription
 * so a broken/undeployed `sdoHmi` UDF cannot crash the app shell.
 */
function SdoHmiPrefetchBridge({
    onImageUrl,
}: {
    onImageUrl: (imageUrl: string | null | undefined) => void;
}) {
    const sdoHmi = useSafeQuery(api.sdoHmi.getLatestSdoHmiImage);
    useEffect(() => {
        onImageUrl(sdoHmi === undefined ? undefined : (sdoHmi?.imageUrl ?? null));
    }, [sdoHmi, onImageUrl]);
    return null;
}

export function TonightPrefetchProvider({ children }: { children: ReactNode }) {
    const { effectiveCoordinates } = useLocation();
    const [sdoImageUrl, setSdoImageUrl] = useState<string | null | undefined>(undefined);
    const [status, setStatus] = useState<PrefetchStatus>(() => ({
        readyUntil: readStoredReadyUntil(),
        lastPrefetchAt: null,
        isPrefetching: false,
        progress: 0,
        error: null,
    }));
    const [autoSkippedReason, setAutoSkippedReason] = useState<
        TonightPrefetchValue["autoSkippedReason"]
    >(null);
    const autoRanRef = useRef(false);
    const runningRef = useRef(false);

    const handleSdoImageUrl = useCallback((imageUrl: string | null | undefined) => {
        setSdoImageUrl(imageUrl);
    }, []);

    const runPrefetch = useCallback(
        async (options?: { force?: boolean }) => {
            if (runningRef.current) return;
            const hints = getConnectionHints();
            if (hints.offline) {
                setStatus((s) => ({
                    ...s,
                    error: "Connect to the internet to download tonight's data.",
                }));
                return;
            }
            if (!options?.force && (hints.saveData || hints.cellular)) {
                setStatus((s) => ({
                    ...s,
                    error: hints.saveData
                        ? "Data saver is on — use Download in Settings if you want to proceed."
                        : "On cellular — use Download in Settings if you want to proceed.",
                }));
                return;
            }

            runningRef.current = true;
            setStatus((s) => ({ ...s, isPrefetching: true, progress: 0, error: null }));

            try {
                setStatus((s) => ({ ...s, progress: 0.2 }));
                // Skip when the SDO query is unavailable (undefined still loading; null = no image).
                if (sdoImageUrl !== undefined) {
                    await ensureSdoHmiImageCached(sdoImageUrl, true);
                }

                setStatus((s) => ({ ...s, progress: 0.5 }));
                if (effectiveCoordinates) {
                    await prefetchMapTiles(effectiveCoordinates.lat, effectiveCoordinates.lon);
                }

                setStatus((s) => ({ ...s, progress: 0.8 }));
                await countFreshCacheEntries();

                const readyUntil = Date.now() + OFFLINE_READY_HOURS * 60 * 60 * 1000;
                writeStoredReadyUntil(readyUntil);
                setStatus({
                    readyUntil,
                    lastPrefetchAt: Date.now(),
                    isPrefetching: false,
                    progress: 1,
                    error: null,
                });
                setAutoSkippedReason(null);
            } catch (error) {
                setStatus((s) => ({
                    ...s,
                    isPrefetching: false,
                    error: error instanceof Error ? error.message : "Prefetch failed",
                }));
            } finally {
                runningRef.current = false;
            }
        },
        [effectiveCoordinates, sdoImageUrl],
    );

    // Auto-prefetch for the general public on Wi‑Fi (skips cellular / save-data).
    useEffect(() => {
        if (autoRanRef.current) return;
        if (typeof navigator === "undefined") return;

        const readyUntil = readStoredReadyUntil();
        if (readyUntil && readyUntil > Date.now()) {
            setStatus((s) => ({ ...s, readyUntil }));
            return;
        }

        const hints = getConnectionHints();
        if (hints.offline) {
            setAutoSkippedReason("offline");
            return;
        }
        if (hints.saveData) {
            setAutoSkippedReason("saveData");
            return;
        }
        if (hints.cellular) {
            setAutoSkippedReason("cellular");
            return;
        }

        autoRanRef.current = true;
        const timer = setTimeout(() => {
            void runPrefetch();
        }, 1500);
        return () => clearTimeout(timer);
    }, [runPrefetch]);

    const value = useMemo<TonightPrefetchValue>(() => {
        const isReady = Boolean(status.readyUntil && status.readyUntil > Date.now());
        return {
            ...status,
            isReady,
            autoSkippedReason,
            runPrefetch,
        };
    }, [status, autoSkippedReason, runPrefetch]);

    return (
        <TonightPrefetchContext.Provider value={value}>
            <SdoHmiPrefetchBridge onImageUrl={handleSdoImageUrl} />
            {children}
        </TonightPrefetchContext.Provider>
    );
}

export function useTonightPrefetch(): TonightPrefetchValue {
    const ctx = useContext(TonightPrefetchContext);
    if (!ctx) {
        throw new Error("useTonightPrefetch must be used within TonightPrefetchProvider");
    }
    return ctx;
}
